import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import {
  TrendingUp, TrendingDown, Target, BarChart3, Zap, Users, Shield,
  AlertTriangle, ArrowUp, ArrowDown, Minus, Info, Loader2,
  Activity, Radio, CheckCircle2
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, AreaChart, Area, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  PieChart as RechartsPieChart, Pie
} from 'recharts';

const PALETTE = {
  emerald: '#10B981',
  teal: '#14B8A6',
  sky: '#0EA5E9',
  indigo: '#6366F1',
  amber: '#F59E0B',
  rose: '#F43F5E',
  slate: '#64748B',
  violet: '#8B5CF6',
};

const ADOPTION_COLORS = {
  high_adoption: '#10B981',
  moderate_adoption: '#14B8A6',
  passive: '#0EA5E9',
  resistant: '#F59E0B',
  strongly_resistant: '#F43F5E',
};

const CURVE_COLORS = {
  innovators: '#6366F1',
  early_adopters: '#10B981',
  early_majority: '#0EA5E9',
  late_majority: '#F59E0B',
  resistant: '#F43F5E',
};

const RISK_COLORS = { low: '#10B981', moderate: '#F59E0B', high: '#F43F5E' };

const CONTEXT_OPTIONS = [
  { value: 'consumer', label: 'Consumer' },
  { value: 'b2b', label: 'B2B' },
  { value: 'healthcare', label: 'Healthcare' },
];

export default function MarketLabViews({ simulation, aggregation, population, stimulus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextType, setContextType] = useState('consumer');

  useEffect(() => {
    if (!simulation?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.get(`/simulations/${simulation.id}/commercial-analysis`, {
      params: { context_type: contextType },
      _silent: true,
    })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load commercial analysis'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [simulation?.id, contextType]);

  if (!simulation) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="marketlab-empty">
        <Info className="w-5 h-5 mr-2" />Select a simulation to view Commercial Analysis
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="marketlab-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mr-2" />
        <span className="text-muted-foreground">Computing commercial metrics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-negative" data-testid="marketlab-error">
        <AlertTriangle className="w-5 h-5 mr-2" />{error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="marketlab-commercial">
      {/* Context Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Commercial Analysis</h2>
          <p className="text-sm text-muted-foreground">{data.stimulus_name || simulation.name}</p>
        </div>
        <Select value={contextType} onValueChange={setContextType}>
          <SelectTrigger className="w-40" data-testid="context-type-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONTEXT_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ExecutiveSnapshot data={data} />
      <ResponseLandscape data={data} aggregation={aggregation} />
      <AdoptionCurveSection data={data} />
      <ChannelOptimizationSection data={data} />
      <RiskResistanceSection data={data} />
      <MarketOpportunityScore data={data} />
      <DecisionDashboard data={data} />
    </div>
  );
}

/* ─── Executive Snapshot ─────────────────────────────────────────────── */

function ExecutiveSnapshot({ data }) {
  const { adoption, adoption_labels, avg_confidence, predictive_stability, total_respondents } = data;
  const stabilityColor = { high: 'text-positive-soft-foreground', moderate: 'text-warning', low: 'text-negative' };

  const topTier = Object.entries(adoption || {}).sort((a, b) => b[1] - a[1])[0];
  const topLabel = adoption_labels?.[topTier?.[0]] || topTier?.[0] || '-';
  const topPct = topTier?.[1] || 0;

  return (
    <Card data-testid="executive-snapshot">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4 text-indigo-500" />Executive Snapshot
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard label="Dominant Tier" value={topLabel} sub={`${topPct}%`} icon={<Target className="w-4 h-4 text-positive" />} testId="metric-dominant-tier" />
          <MetricCard label="Respondents" value={total_respondents?.toLocaleString()} icon={<Users className="w-4 h-4 text-blue-500" />} testId="metric-respondents" />
          <MetricCard label="Avg Confidence" value={`${(avg_confidence * 100).toFixed(0)}%`} icon={<Shield className="w-4 h-4 text-blue-500" />} testId="metric-confidence" />
          <MetricCard label="Stability" value={predictive_stability} className={stabilityColor[predictive_stability]} icon={<Zap className="w-4 h-4 text-warning" />} testId="metric-stability" />
        </div>

        {/* Adoption breakdown bar */}
        <div className="mt-5">
          <p className="text-xs font-medium text-muted-foreground mb-2">Adoption Breakdown</p>
          <div className="flex h-7 rounded-md overflow-hidden">
            {Object.entries(adoption || {}).map(([tier, pct]) => (
              pct > 0 && (
                <div
                  key={tier}
                  className="flex items-center justify-center text-[10px] font-semibold text-white transition-all"
                  style={{ width: `${pct}%`, backgroundColor: ADOPTION_COLORS[tier] || PALETTE.slate, minWidth: pct > 3 ? 'auto' : 0 }}
                  title={`${adoption_labels?.[tier] || tier}: ${pct}%`}
                >
                  {pct >= 8 ? `${pct}%` : ''}
                </div>
              )
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {Object.entries(adoption || {}).map(([tier, pct]) => (
              <div key={tier} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ADOPTION_COLORS[tier] }} />
                {adoption_labels?.[tier] || tier} ({pct}%)
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Adoption Curve (Rogers Diffusion) ──────────────────────────────── */

function AdoptionCurveSection({ data }) {
  const { adoption_curve } = data;
  if (!adoption_curve) return null;

  const curveLabels = {
    innovators: 'Innovators',
    early_adopters: 'Early Adopters',
    early_majority: 'Early Majority',
    late_majority: 'Late Majority',
    resistant: 'Resistant',
  };

  const chartData = Object.entries(adoption_curve).map(([seg, pct]) => ({
    name: curveLabels[seg] || seg,
    value: pct,
    fill: CURVE_COLORS[seg] || PALETTE.slate,
  }));

  // Build cumulative area data
  let cumulative = 0;
  const areaData = chartData.map(d => {
    cumulative += d.value;
    return { name: d.name, cumulative: Math.round(cumulative * 10) / 10, segment: d.value };
  });

  return (
    <Card data-testid="adoption-curve-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-positive" />Adoption Curve
        </CardTitle>
        <CardDescription>Rogers diffusion segmentation of respondents</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar chart */}
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barSize={36}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => `${v}%`} />
                <Tooltip formatter={v => [`${v}%`, 'Share']} contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Cumulative S-curve */}
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={areaData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => `${v}%`} domain={[0, 100]} />
                <Tooltip formatter={v => [`${v}%`]} contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                <Area type="monotone" dataKey="cumulative" stroke={PALETTE.indigo} fill={PALETTE.indigo} fillOpacity={0.15} strokeWidth={2} name="Cumulative Adoption" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Segment cards */}
        <div className="grid grid-cols-5 gap-2 mt-4">
          {chartData.map(d => (
            <div key={d.name} className="rounded-lg border p-2.5 text-center" data-testid={`curve-segment-${d.name.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="text-lg font-bold" style={{ color: d.fill }}>{d.value}%</div>
              <div className="text-[11px] text-muted-foreground leading-tight">{d.name}</div>
            </div>
          ))}
        </div>

        {/* ─── Early Adopter Target Size ─────────────────────────────── */}
        {(() => {
          const innovatorsPct = adoption_curve.innovators || 0;
          const earlyAdoptersPct = adoption_curve.early_adopters || 0;
          const earlyPoolPct = innovatorsPct + earlyAdoptersPct;
          const totalResp = data.total_respondents || 0;
          const earlyCount = Math.round(totalResp * earlyPoolPct / 100);

          return (
            <div className="mt-4 p-3.5 rounded-lg border-2 border-indigo-200/60 bg-indigo-50/30" data-testid="early-adopter-target">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-semibold text-indigo-800">Estimated Early Adopter Target Pool</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xl font-bold text-indigo-700">{earlyPoolPct.toFixed(1)}%</p>
                  <p className="text-[10px] text-muted-foreground">Innovators + Early Adopters</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-indigo-700">{earlyCount.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">Target Individuals</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-indigo-700">
                    {innovatorsPct.toFixed(1)}% / {earlyAdoptersPct.toFixed(1)}%
                  </p>
                  <p className="text-[10px] text-muted-foreground">Innovators / Early Adopters</p>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 text-center">
                Innovators and Early Adopters represent the highest-value initial target audience for launch campaigns.
              </p>
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

/* ─── Channel Optimization ───────────────────────────────────────────── */

function ChannelOptimizationSection({ data }) {
  const { channels } = data;
  if (!channels) return null;

  const channelLabels = {
    email: 'Email', social_media: 'Social Media', digital_ads: 'Digital Ads',
    in_store: 'In-Store', influencer: 'Influencer', webinar: 'Webinar',
    rep_visit: 'Rep Visit', whitepaper: 'Whitepaper',
    scientific_publication: 'Scientific Publication', digital_portal: 'Digital Portal',
  };

  const chartData = (channels.channels || []).map(ch => ({
    name: channelLabels[ch] || ch,
    score: channels.overall?.[ch] || 0,
  })).sort((a, b) => b.score - a.score);

  const maxScore = Math.max(...chartData.map(d => d.score), 1);

  return (
    <Card data-testid="channel-optimization-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Radio className="w-4 h-4 text-blue-500" />Channel Optimization
        </CardTitle>
        <CardDescription>Estimated channel responsiveness based on adoption profile</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {chartData.map((ch, i) => (
            <div key={ch.name} className="flex items-center gap-3" data-testid={`channel-${ch.name.toLowerCase().replace(/\s/g, '-')}`}>
              <span className="text-sm w-36 truncate text-foreground font-medium">{ch.name}</span>
              <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${(ch.score / maxScore) * 100}%`,
                    backgroundColor: i === 0 ? PALETTE.emerald : i < 3 ? PALETTE.teal : PALETTE.sky,
                  }}
                />
              </div>
              <span className="text-sm font-semibold w-12 text-right text-foreground">{ch.score.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Risk & Resistance ──────────────────────────────────────────────── */

function RiskResistanceSection({ data }) {
  const { risk } = data;
  if (!risk) return null;

  const riskColor = RISK_COLORS[risk.risk_level] || PALETTE.slate;

  const gaugeData = [
    { label: 'Polarization', value: risk.polarization_score, max: 1, fmt: v => v.toFixed(2) },
    { label: 'Backlash Risk', value: risk.minority_backlash_risk, max: 100, fmt: v => `${v.toFixed(0)}%` },
    { label: 'Volatility', value: risk.volatility_index, max: 1, fmt: v => v.toFixed(2) },
  ];

  return (
    <Card data-testid="risk-resistance-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" style={{ color: riskColor }} />Risk & Resistance
            </CardTitle>
            <CardDescription>Polarization, backlash risk, and response volatility</CardDescription>
          </div>
          <Badge
            className="text-xs"
            style={{ backgroundColor: `${riskColor}20`, color: riskColor, borderColor: riskColor }}
            data-testid="risk-level-badge"
          >
            {risk.risk_level.toUpperCase()} RISK
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          {gaugeData.map(g => {
            const pct = Math.min((g.value / g.max) * 100, 100);
            const color = pct > 60 ? PALETTE.rose : pct > 30 ? PALETTE.amber : PALETTE.emerald;
            return (
              <div key={g.label} className="text-center" data-testid={`risk-${g.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="text-xl font-bold" style={{ color }}>{g.fmt(g.value)}</div>
                <p className="text-xs text-muted-foreground mt-0.5">{g.label}</p>
                <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Segment volatility */}
        {risk.segment_volatility && Object.keys(risk.segment_volatility).length > 0 && (() => {
          const values = Object.values(risk.segment_volatility);
          const avgVol = values.reduce((a, b) => a + b, 0) / values.length;
          const spread = Math.max(...values) - Math.min(...values);
          const isUniform = spread < 0.05;
          
          return (
            <div className="mt-5">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs font-medium text-muted-foreground">Segment Volatility</p>
                {isUniform && (
                  <Badge variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800">
                    Uniform Response — Low segment differentiation
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {Object.entries(risk.segment_volatility)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 12)
                  .map(([seg, vol]) => {
                    const pct = Math.min(vol * 100, 100);
                    const color = pct > 60 ? PALETTE.rose : pct > 30 ? PALETTE.amber : PALETTE.emerald;
                    return (
                      <div key={seg} className="flex items-center gap-2 text-xs">
                        <span className="truncate flex-1 text-muted-foreground">{seg.split(':').pop()}</span>
                        <span className="font-medium" style={{ color }}>{vol.toFixed(2)}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

/* ─── Response Landscape ──────────────────────────────────────────────── */

function ResponseLandscape({ data, aggregation }) {
  const { adoption, total_respondents, risk } = data;
  
  // Calculate top/bottom box from adoption tiers
  const topBox = (adoption?.high_adoption || 0) + (adoption?.moderate_adoption || 0);
  const bottomBox = (adoption?.resistant || 0) + (adoption?.strongly_resistant || 0);
  const neutralPct = adoption?.passive || 0;
  const polarization = risk?.polarization_score || 0;

  return (
    <Card data-testid="response-landscape">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-500" />Response Landscape
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-lg border border-positive-border bg-positive-soft p-3 text-center">
            <p className="text-xl font-bold text-positive-soft-foreground">{topBox}%</p>
            <p className="text-xs text-muted-foreground">Top Box (Positive)</p>
          </div>
          <div className="rounded-lg border border-negative-border bg-negative-soft p-3 text-center">
            <p className="text-xl font-bold text-negative-soft-foreground">{bottomBox}%</p>
            <p className="text-xs text-muted-foreground">Bottom Box (Negative)</p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{neutralPct}%</p>
            <p className="text-xs text-muted-foreground">Neutrality</p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <p className="text-xl font-bold" style={{ color: polarization > 0.5 ? PALETTE.rose : polarization > 0.25 ? PALETTE.amber : PALETTE.emerald }}>
              {(polarization * 100).toFixed(0)}%
            </p>
            <p className="text-xs text-muted-foreground">Polarization</p>
          </div>
        </div>

        {/* Visual bar */}
        <div className="mt-4">
          <div className="flex h-6 rounded-md overflow-hidden">
            <div className="bg-positive flex items-center justify-center text-[10px] text-white font-medium" style={{ width: `${topBox}%` }}>
              {topBox >= 10 ? `${topBox}%` : ''}
            </div>
            <div className="bg-blue-400 flex items-center justify-center text-[10px] text-white font-medium" style={{ width: `${neutralPct}%` }}>
              {neutralPct >= 10 ? `${neutralPct}%` : ''}
            </div>
            <div className="bg-negative flex items-center justify-center text-[10px] text-white font-medium" style={{ width: `${bottomBox}%` }}>
              {bottomBox >= 10 ? `${bottomBox}%` : ''}
            </div>
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>Positive</span><span>Neutral</span><span>Negative</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Market Opportunity Score ────────────────────────────────────────── */

function MarketOpportunityScore({ data }) {
  const { adoption, risk, avg_confidence } = data;
  
  // Market Opportunity Score = intent × confidence × engagement × (1 − resistance)
  const intentScore = ((adoption?.high_adoption || 0) + (adoption?.moderate_adoption || 0)) / 100;
  const confidenceScore = avg_confidence || 0.5;
  const resistanceRate = ((adoption?.resistant || 0) + (adoption?.strongly_resistant || 0)) / 100;
  const neutralRate = (adoption?.passive || 0) / 100;
  const engagementScore = 1 - neutralRate;

  const rawScore = intentScore * confidenceScore * engagementScore * (1 - resistanceRate);
  const mosScore = Math.round(rawScore * 100);

  const scoreColor = mosScore >= 40 ? '#10B981' : mosScore >= 20 ? '#F59E0B' : '#EF4444';
  const scoreLabel = mosScore >= 40 ? 'Strong' : mosScore >= 20 ? 'Moderate' : 'Weak';

  const components = [
    { label: 'Positive Intent', value: intentScore, pct: (intentScore * 100).toFixed(0), desc: 'High + Moderate adoption' },
    { label: 'Confidence', value: confidenceScore, pct: (confidenceScore * 100).toFixed(0), desc: 'Avg response confidence' },
    { label: 'Engagement', value: engagementScore, pct: (engagementScore * 100).toFixed(0), desc: '1 − Passive/Undecided' },
    { label: '1 − Resistance', value: 1 - resistanceRate, pct: ((1 - resistanceRate) * 100).toFixed(0), desc: 'Non-resistant share' },
  ];

  return (
    <Card data-testid="market-opportunity-score">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="w-4 h-4 text-indigo-500" />Market Opportunity Score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-8">
          {/* Score circle */}
          <div className="relative flex-shrink-0">
            <svg width={120} height={120} className="transform -rotate-90">
              <circle cx={60} cy={60} r={50} fill="none" stroke="currentColor" className="text-muted/20" strokeWidth={8} />
              <circle cx={60} cy={60} r={50} fill="none" stroke={scoreColor} strokeWidth={8}
                strokeDasharray={2 * Math.PI * 50} strokeDashoffset={2 * Math.PI * 50 * (1 - mosScore / 100)}
                strokeLinecap="round" className="transition-all duration-1000" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold" style={{ color: scoreColor }}>{mosScore}</span>
              <span className="text-[10px] text-muted-foreground">{scoreLabel}</span>
            </div>
          </div>

          {/* Component breakdown */}
          <div className="flex-1 space-y-2.5">
            {components.map(c => (
              <div key={c.label} className="flex items-center gap-2">
                <div className="w-28 text-sm text-muted-foreground group relative cursor-help">
                  {c.label}
                  {c.desc && <span className="hidden group-hover:block absolute bottom-full left-0 bg-popover text-popover-foreground text-[10px] p-1.5 rounded shadow-lg border border-border whitespace-nowrap z-10">{c.desc}</span>}
                </div>
                <div className="flex-1">
                  <Progress value={c.value * 100} className="h-1.5" />
                </div>
                <span className="w-10 text-right text-sm font-medium text-foreground">{c.pct}%</span>
              </div>
            ))}
            <Separator className="my-2" />
            <p className="text-[10px] text-muted-foreground italic">
              MOS = Positive Intent x Confidence x Engagement x (1 - Resistance)
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Decision Dashboard ─────────────────────────────────────────────── */

function DecisionDashboard({ data }) {
  const { adoption, risk, avg_confidence } = data;
  
  const intentScore = ((adoption?.high_adoption || 0) + (adoption?.moderate_adoption || 0)) / 100;
  const resistanceRate = ((adoption?.resistant || 0) + (adoption?.strongly_resistant || 0)) / 100;
  const neutralRate = (adoption?.passive || 0) / 100;
  const engagementScore = 1 - neutralRate;
  const rawScore = intentScore * (avg_confidence || 0.5) * engagementScore * (1 - resistanceRate);
  const mosScore = Math.round(rawScore * 100);

  const riskLevel = risk?.risk_level || 'low';

  let recommendation, label, rationale, color;
  if (mosScore >= 35 && riskLevel !== 'high') {
    recommendation = 'launch'; label = 'Launch'; color = '#10B981';
    rationale = 'Strong market opportunity with acceptable risk profile. Proceed with launch strategy.';
  } else if (mosScore >= 15 && riskLevel !== 'high') {
    recommendation = 'refine'; label = 'Refine'; color = '#F59E0B';
    rationale = 'Moderate opportunity exists but requires strategic refinement before launch.';
  } else {
    recommendation = 'reconsider'; label = 'Reconsider'; color = '#EF4444';
    rationale = 'Significant market resistance or risk. Reconsider positioning or target segment.';
  }

  const DECISION_ICONS = { launch: TrendingUp, refine: AlertTriangle, reconsider: TrendingDown };
  const Icon = DECISION_ICONS[recommendation];

  return (
    <Card className="border-2" style={{ borderColor: `${color}40` }} data-testid="decision-dashboard">
      <CardContent className="py-6">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-sm"
            style={{ backgroundColor: color }}>
            <Icon className="w-5 h-5" />
            {label}
          </div>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">{rationale}</p>
          <div className="flex justify-center gap-3 mt-2">
            <Badge variant="outline" className="text-[10px]">MOS: {mosScore}/100</Badge>
            <Badge variant="outline" className="text-[10px]" style={{ color: RISK_COLORS[riskLevel], borderColor: RISK_COLORS[riskLevel] }}>
              Risk: {riskLevel}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Shared MetricCard ──────────────────────────────────────────────── */

function MetricCard({ label, value, sub, icon, className, testId }) {
  return (
    <div className="rounded-lg border p-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className={cn("text-lg font-bold text-foreground", className)}>{value}</div>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export { PALETTE as MARKETLAB_COLORS };
